/* Waste Walk / Gemba Tracker — TIMWOODS log with photos & severity */
import ToolWizard from "./ToolWizard";
import { Plus, Trash2, FileText, MapPin } from "lucide-react";

const WASTES = [
  { id: "T", label: "Transport",        color: "#0EA5E9" },
  { id: "I", label: "Inventory",        color: "#6366F1" },
  { id: "M", label: "Motion",           color: "#A855F7" },
  { id: "W", label: "Waiting",          color: "#D97706" },
  { id: "O", label: "Overprocessing",   color: "#DC2626" },
  { id: "P", label: "Overproduction",   color: "#7C2D12" },
  { id: "D", label: "Defects",          color: "#BE123C" },
  { id: "S", label: "Skills (under-utilised)", color: "#16A34A" },
];
const WASTE_LABEL = Object.fromEntries(WASTES.map(w => [w.id, w.label]));
const WASTE_COLOR = Object.fromEntries(WASTES.map(w => [w.id, w.color]));
const SEVERITY = [{ id: "H", label: "High" }, { id: "M", label: "Medium" }, { id: "L", label: "Low" }];

export default function WasteWalkWizard({ project, onSave, onExportPDF }) {
  const steps = [
    {
      key: "setup", label: "Setup",
      render: ({ data, updateData }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ww-setup">
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Gemba area / floor</label><input value={data.area || ""} onChange={e => updateData({ area: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Walked by</label><input value={data.observer || ""} onChange={e => updateData({ observer: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs font-semibold text-sage-600 mb-1">Date</label><input type="date" value={data.walk_date || ""} onChange={e => updateData({ walk_date: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="block text-xs font-semibold text-sage-600 mb-1">Walk objective</label><textarea value={data.objective || ""} onChange={e => updateData({ objective: e.target.value })} className="w-full border border-sage-200 rounded-md px-3 py-2 text-sm min-h-[60px]" /></div>
        </div>
      ),
    },
    {
      key: "log", label: "Waste Log",
      render: ({ data, updateData }) => {
        const items = data.observations || [];
        const add = () => updateData({ observations: [...items, { waste_type: "W", location: "", description: "", severity: "M", photo_url: "", suggested_fix: "", cost_impact: 0 }] });
        const upd = (i, p) => updateData({ observations: items.map((m, idx) => idx === i ? { ...m, ...p } : m) });
        const rm  = (i) => updateData({ observations: items.filter((_, idx) => idx !== i) });
        return (
          <div className="space-y-2" data-testid="ww-log">
            {items.map((it, i) => (
              <div key={i} className="bg-white border-l-4 border-sage-200 rounded-md p-3 space-y-2" style={{ borderLeftColor: WASTE_COLOR[it.waste_type] }} data-testid={`ww-obs-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <select data-testid={`ww-obs-${i}-type`} value={it.waste_type} onChange={e => upd(i, { waste_type: e.target.value })} className="md:col-span-3 border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white font-semibold" style={{ color: WASTE_COLOR[it.waste_type] }}>
                    {WASTES.map(w => <option key={w.id} value={w.id}>{w.id} — {w.label}</option>)}
                  </select>
                  <input data-testid={`ww-obs-${i}-loc`} placeholder="Location (e.g. Floor 2 · Aisle 3)" value={it.location} onChange={e => upd(i, { location: e.target.value })} className="md:col-span-4 border border-sage-200 rounded-md px-2 py-1.5 text-xs" />
                  <select value={it.severity} onChange={e => upd(i, { severity: e.target.value })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-xs bg-white">
                    {SEVERITY.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <input type="number" placeholder="Cost $" value={it.cost_impact} onChange={e => upd(i, { cost_impact: Number(e.target.value) })} className="md:col-span-2 border border-sage-200 rounded-md px-2 py-1.5 text-xs" />
                  <button onClick={() => rm(i)} className="text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea data-testid={`ww-obs-${i}-desc`} placeholder="What was observed?" value={it.description} onChange={e => upd(i, { description: e.target.value })} className="w-full border border-sage-200 rounded-md px-2 py-1.5 text-xs min-h-[50px]" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input placeholder="Photo URL (optional)" value={it.photo_url || ""} onChange={e => upd(i, { photo_url: e.target.value })} className="border border-sage-100 rounded-md px-2 py-1 text-xs" />
                  <input placeholder="Suggested fix / countermeasure" value={it.suggested_fix || ""} onChange={e => upd(i, { suggested_fix: e.target.value })} className="border border-sage-100 rounded-md px-2 py-1 text-xs" />
                </div>
              </div>
            ))}
            <button data-testid="ww-add-obs" onClick={add} className="flex items-center gap-1 border border-dashed border-sage-300 text-sage-600 rounded-md px-3 py-2 text-sm hover:bg-sage-50 w-full justify-center"><Plus className="w-3.5 h-3.5" /> Log waste observation</button>
          </div>
        );
      },
    },
    {
      key: "diagnostic", label: "Diagnostic",
      render: ({ data }) => {
        const obs = data.observations || [];
        const totalCost = obs.reduce((a, o) => a + Number(o.cost_impact || 0), 0);
        const heat = WASTES.map(w => {
          const list = obs.filter(o => o.waste_type === w.id);
          const cost = list.reduce((a, o) => a + Number(o.cost_impact || 0), 0);
          return { ...w, count: list.length, cost };
        });
        const maxN = Math.max(1, ...heat.map(h => h.count));
        const top = [...obs].sort((a, b) => Number(b.cost_impact || 0) - Number(a.cost_impact || 0)).slice(0, 5);
        return (
          <div className="space-y-4" data-testid="ww-diagnostic">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Total observations</p><p className="font-heading text-2xl font-bold text-sage-900">{obs.length}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">High severity</p><p className="font-heading text-2xl font-bold text-red-700">{obs.filter(o => o.severity === "H").length}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">Cost impact / yr</p><p className="font-heading text-2xl font-bold text-sage-900">${totalCost.toLocaleString()}</p></div>
              <div className="bg-sage-50 rounded-md p-3"><p className="text-xs text-sage-500 uppercase tracking-wider font-semibold">With photos</p><p className="font-heading text-2xl font-bold text-sage-900">{obs.filter(o => o.photo_url).length}</p></div>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">8-waste heat-map</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {heat.map(w => (
                  <div key={w.id} className="rounded-md p-3 border-2" style={{ borderColor: w.color, backgroundColor: `${w.color}${Math.round((w.count / maxN) * 35 + 8).toString(16).padStart(2, "0")}` }}>
                    <p className="text-xs font-bold" style={{ color: w.color }}>{w.id} — {w.label}</p>
                    <p className="font-heading text-2xl font-bold text-sage-900">{w.count}</p>
                    <p className="text-xs text-sage-600">${w.cost.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
            {top.length > 0 && (
              <div>
                <h4 className="font-heading text-sm font-semibold text-sage-800 mb-2">Top cost-impact observations</h4>
                <div className="space-y-1.5">
                  {top.map((o, i) => (
                    <div key={i} className="bg-white border border-sage-200 rounded-md p-2 text-xs flex items-center gap-2">
                      <span className="rounded px-1.5 py-0.5 text-white text-[10px] font-bold" style={{ backgroundColor: WASTE_COLOR[o.waste_type] }}>{o.waste_type}</span>
                      <MapPin className="w-3 h-3 text-sage-500" />
                      <span className="text-sage-600">{o.location || "—"}</span>
                      <span className="flex-1 text-sage-900">{o.description || "—"}</span>
                      <span className="font-mono font-bold text-sage-900">${Number(o.cost_impact || 0).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="export-ww-pdf" onClick={() => onExportPDF(data)} className="bg-sage-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-sage-700 flex items-center gap-2"><FileText className="w-4 h-4" /> Export PDF</button>
          </div>
        );
      },
    },
  ];
  return <ToolWizard project={project} steps={steps} onSave={onSave} completedLabel="Finish Waste Walk" />;
}

export { WASTES, WASTE_LABEL, WASTE_COLOR };
